
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageTransition from "@/components/PageTransition";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Bell, Mail, MessageSquare, Lock, LogOut } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const Account = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({
    email: true,
    priceDrops: true,
    restocks: false,
    deals: true,
  });

  const handleSave = () => {
    toast.success("Settings saved", {
      description: "Your account settings have been updated.",
    });
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Error logging out:", error);
        toast.error("Failed to log out");
        return;
      }
      
      toast.success("Logged out successfully");
      navigate("/");
    } catch (err) {
      console.error("Unexpected error during logout:", err);
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <PageTransition>
      <div className="space-y-8 max-w-2xl mx-auto">
        <section>
          <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Account Settings
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            Manage Your Account
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Customize your experience and manage your PricePal account settings.
          </p>
        </section>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Preferences
            </CardTitle>
            <CardDescription>
              Manage how and when PricePal notifies you.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="email-notifications">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive updates via email
                </p>
              </div>
              <Switch
                id="email-notifications"
                checked={notifications.email}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, email: checked })
                }
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="price-drops">Price Drop Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when prices drop on your wishlist items
                </p>
              </div>
              <Switch
                id="price-drops"
                checked={notifications.priceDrops}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, priceDrops: checked })
                }
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="restocks">Restock Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when out-of-stock items become available
                </p>
              </div>
              <Switch
                id="restocks"
                checked={notifications.restocks}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, restocks: checked })
                }
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="deals">Deal Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive alerts about special deals and promotions
                </p>
              </div>
              <Switch
                id="deals"
                checked={notifications.deals}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, deals: checked })
                }
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Privacy & Security
            </CardTitle>
            <CardDescription>
              Manage your privacy settings and security options.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-20 flex items-center justify-center bg-secondary/50 rounded-md">
              <p className="text-muted-foreground">Privacy settings coming soon</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageTransition>
  );
};

export default Account;
